import { LOGIN_PROVIDER } from '@web3auth/auth';
import classNames from 'classnames';
import { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { capitalizeFirstLetter } from '../config.js';
import { ThemedContext } from '../context/ThemeContext.js';
import i18nInstance from '../localeImport.js';
import Button from './Button/Button.js';
import Image from './Image.js';
import { jsxs, jsx } from 'react/jsx-runtime';

function getProviderIcon(method, isDark, isPrimaryBtn) {
  const imageId = method === LOGIN_PROVIDER.TWITTER ? `login-twitter-x${isDark ? "-light" : "-dark"}` : `login-${method}${isDark ? "-light" : "-dark"}`;
  const hoverId = method === LOGIN_PROVIDER.APPLE || method === LOGIN_PROVIDER.GITHUB || method === LOGIN_PROVIDER.TWITTER ? imageId : `login-${method}-active`;
  if (isPrimaryBtn) {
    return /*#__PURE__*/jsx(Image, {
      width: "20",
      imageId: hoverId,
      hoverImageId: hoverId,
      isButton: true
    });
  }
  return /*#__PURE__*/jsx(Image, {
    width: "20",
    imageId: imageId,
    hoverImageId: hoverId,
    isButton: true
  });
}
function SocialLogins(props) {
  const [canShowMore, setCanShowMore] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    socialLoginsConfig = {
      loginMethods: {},
      loginMethodsOrder: [],
      adapter: "",
      uiConfig: {}
    },
    handleSocialLoginClick
  } = props;
  const {
    isDark
  } = useContext(ThemedContext);
  const [t] = useTranslation(undefined, {
    i18n: i18nInstance
  });

  // Too small a function to use `useCallback`
  const expandClickHandler = () => {
    setIsExpanded(!isExpanded);
  };
  useEffect(() => {
    const maxOptions = Object.keys(socialLoginsConfig.loginMethods).filter(loginMethodKey => {
      return socialLoginsConfig.loginMethods[loginMethodKey].showOnModal;
    });
    setCanShowMore(maxOptions.length > 4);
  }, [socialLoginsConfig.loginMethods]);
  const adapterListClass = classNames("w3a-adapter-list", "w3ajs-socials-adapters", !isExpanded ? " w3a-adapter-list--shrink" : "");
  const adapterButtonClass = classNames("w3a-button-expand", "w3ajs-button-expand", isExpanded ? "w3a-button--rotate" : "");
  const adapterExpandText = isExpanded ? t("modal.social.view-less") : t("modal.social.view-more");
  const loginMethodsCount = Object.keys(socialLoginsConfig.loginMethods).length + 1;
  const restrictedLoginMethods = [LOGIN_PROVIDER.WEBAUTHN, LOGIN_PROVIDER.JWT, LOGIN_PROVIDER.SMS_PASSWORDLESS, LOGIN_PROVIDER.EMAIL_PASSWORDLESS, LOGIN_PROVIDER.AUTHENTICATOR, LOGIN_PROVIDER.PASSKEYS];
  return /*#__PURE__*/jsxs("div", {
    className: "w3ajs-social-logins w3a-group",
    children: [/*#__PURE__*/jsx("ul", {
      className: adapterListClass,
      children: Object.keys(socialLoginsConfig.loginMethods).map(method => {
        var _socialLoginsConfig$u, _socialLoginsConfig$u2;
        const name = capitalizeFirstLetter(socialLoginsConfig.loginMethods[method].name || method);
        const orderIndex = socialLoginsConfig.loginMethodsOrder.indexOf(method) + 1;
        const order = orderIndex || Object.keys(socialLoginsConfig.loginMethods).length + 1;
        const isMainOption = socialLoginsConfig.loginMethods[method].mainOption;
        const isPrimaryBtn = (socialLoginsConfig === null || socialLoginsConfig === void 0 || (_socialLoginsConfig$u = socialLoginsConfig.uiConfig) === null || _socialLoginsConfig$u === void 0 ? void 0 : _socialLoginsConfig$u.primaryButton) === "socialLogin" && order === 1;
        const providerIcon = getProviderIcon(method, isDark, isPrimaryBtn);
        if (socialLoginsConfig.loginMethods[method].showOnModal === false || restrictedLoginMethods.includes(method)) {
          return null;
        }
        const loginMethodSpan = classNames("w3a-adapter-item", (socialLoginsConfig === null || socialLoginsConfig === void 0 || (_socialLoginsConfig$u2 = socialLoginsConfig.uiConfig) === null || _socialLoginsConfig$u2 === void 0 ? void 0 : _socialLoginsConfig$u2.loginGridCol) === 2 ? "w3a--col-span-3" : "w3a--col-span-2");
        if (isMainOption || order === 1) {
          return /*#__PURE__*/jsx("li", {
            className: "w3a--col-span-6 w3a-adapter-item",
            style: {
              order
            },
            children: /*#__PURE__*/jsxs(Button, {
              variant: "secondary",
              onClick: () => handleSocialLoginClick({
                adapter: socialLoginsConfig.adapter,
                loginParams: {
                  loginProvider: method,
                  name,
                  login_hint: ""
                }
              }),
              className: "w3a--w-full",
              title: name,
              children: [providerIcon, /*#__PURE__*/jsx("p", {
                className: "w3a--ml-2",
                children: t("modal.social.continueCustom", {
                  adapter: name
                })
              })]
            })
          }, method);
        }
        return /*#__PURE__*/jsx("li", {
          className: loginMethodSpan,
          style: {
            order: order + loginMethodsCount
          },
          children: /*#__PURE__*/jsx(Button, {
            variant: "secondary",
            onClick: () => handleSocialLoginClick({
              adapter: socialLoginsConfig.adapter,
              loginParams: {
                loginProvider: method,
                name,
                login_hint: ""
              }
            }),
            className: "w3a--w-full",
            title: name,
            children: providerIcon
          })
        }, method);
      })
    }), /*#__PURE__*/jsx("div", {
      className: "w3a-social__policy",
      children: t("modal.social.policy")
    }), canShowMore && /*#__PURE__*/jsx("div", {
      className: "w3a--text-right",
      children: /*#__PURE__*/jsx("button", {
        type: "button",
        className: adapterButtonClass,
        onClick: expandClickHandler,
        children: /*#__PURE__*/jsx("span", {
          className: "w3ajs-button-expand-text",
          children: adapterExpandText
        })
      })
    })]
  });
}

export { SocialLogins as default };
